import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { adaptToBoard } from '@pindd/core'
import { fiveMmBoards, twoSixMmBoards, perlerColors } from '@pindd/data'
import { useProjectStore } from '../store/projectStore'
import { useUIStore } from '../store/uiStore'
import { BoardConfig, BeadColor, PixelShape } from '@pindd/types'
import { Button } from '../components/ui/button'
import { Lock, Info, ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Create() {
  const navigate = useNavigate()
  const { project } = useProjectStore()
  const { setLocked, highlightColor, toggleHighlightColor, setHighlightColor } = useUIStore()
  const [boardConfig, setBoardConfig] = useState<BoardConfig | null>(null)
  const [showInfo, setShowInfo] = useState(false)
  const [unlockProgress, setUnlockProgress] = useState(0)
  const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unlockIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [pixelShape, setPixelShape] = useState<PixelShape>('round')

  const allBoards = [...fiveMmBoards, ...twoSixMmBoards]

  // Auto-lock when entering create page
  useEffect(() => {
    setLocked(true)
  }, [setLocked])

  // Set pixel shape from project
  useEffect(() => {
    if (project?.pixelShape) {
      setPixelShape(project.pixelShape)
    }
  }, [project?.pixelShape])

  useEffect(() => {
    if (project?.pixelatedData && project?.boardSize) {
      const board = allBoards.find((b) => b.id === project.boardSize!.id)
      if (board) {
        const config = adaptToBoard(project.pixelatedData, board, perlerColors)
        setBoardConfig(config)
      }
    }
  }, [project])

  const usedColors = boardConfig
    ? [...new Set(boardConfig.grid.flat().filter(Boolean) as string[])]
        .map((colorId) => perlerColors.find((c) => c.id === colorId))
        .filter(Boolean) as BeadColor[]
    : []

  const colorCounts = boardConfig
    ? usedColors.map((color) => ({
        color,
        count: boardConfig.grid.flat().filter((c) => c === color.id).length,
      }))
    : []

  const totalBeads = colorCounts.reduce((sum, c) => sum + c.count, 0)

  // Calculate cell size based on actual physical bead size
  const getCellSize = () => {
    if (!boardConfig || typeof window === 'undefined') return 10

    // Parse bead size from config
    const beadSizeMm = parseFloat(boardConfig.size.beadSize) || 5

    // Screen DPI (96 is standard, iPad is ~264)
    const dpi = 96 * (window.devicePixelRatio || 1)

    // Convert mm to pixels
    const pixelsPerMm = dpi / 25.4
    const cellSize = Math.round(beadSizeMm * pixelsPerMm)

    // Ensure it fits on screen
    const sidebarWidth = 80
    const padding = 48
    const availableWidth = window.innerWidth - sidebarWidth - padding
    const availableHeight = window.innerHeight - padding
    const totalWidth = cellSize * boardConfig.size.width
    const totalHeight = cellSize * boardConfig.size.height

    // If too large, scale down
    if (totalWidth > availableWidth || totalHeight > availableHeight) {
      const scaleW = availableWidth / boardConfig.size.width
      const scaleH = availableHeight / boardConfig.size.height
      return Math.max(6, Math.floor(Math.min(scaleW, scaleH)))
    }

    return Math.max(6, cellSize)
  }

  const cellSize = getCellSize()

  // Unlock handlers - fixed to use useRef properly
  const handleUnlockStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    setUnlockProgress(0)

    unlockTimerRef.current = setTimeout(() => {
      setLocked(false)
      setUnlockProgress(0)
      if (unlockIntervalRef.current) {
        clearInterval(unlockIntervalRef.current)
        unlockIntervalRef.current = null
      }
    }, 1500)

    unlockIntervalRef.current = setInterval(() => {
      setUnlockProgress((prev) => {
        if (prev >= 100) {
          if (unlockIntervalRef.current) {
            clearInterval(unlockIntervalRef.current)
            unlockIntervalRef.current = null
          }
          return 100
        }
        return prev + (100 / 1500) * 50
      })
    }, 50)
  }, [setLocked])

  const handleUnlockEnd = useCallback(() => {
    if (unlockTimerRef.current) {
      clearTimeout(unlockTimerRef.current)
      unlockTimerRef.current = null
    }
    if (unlockIntervalRef.current) {
      clearInterval(unlockIntervalRef.current)
      unlockIntervalRef.current = null
    }
    setUnlockProgress(0)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current)
      if (unlockIntervalRef.current) clearInterval(unlockIntervalRef.current)
    }
  }, [])

  if (!boardConfig) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">请先完成像素化设置</p>
          <Button onClick={() => navigate('/pixelate')}>返回设置</Button>
        </div>
      </div>
    )
  }

  // Calculate board dimensions with grid numbers
  const gridLabelSize = 20
  const boardWidth = boardConfig.size.width * cellSize
  const boardHeight = boardConfig.size.height * cellSize

  // Determine border radius based on pixel shape
  const borderRadius = pixelShape === 'round' ? '50%' : '2px'

  return (
    <div
      className="fixed inset-0 bg-white overflow-hidden select-none flex"
      style={{ touchAction: 'none' }}
    >
      {/* Board Canvas - Main Area */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="relative flex-shrink-0">
          {/* Grid numbers - Top */}
          <div className="flex" style={{ marginLeft: gridLabelSize }}>
            {Array.from({ length: boardConfig.size.width }).map((_, x) => (
              <div
                key={`top-${x}`}
                className="text-[10px] text-gray-400 text-center flex-shrink-0"
                style={{ width: cellSize }}
              >
                {x % 5 === 0 ? x : ''}
              </div>
            ))}
          </div>

          <div className="flex">
            {/* Grid numbers - Left */}
            <div className="flex flex-col" style={{ width: gridLabelSize }}>
              {Array.from({ length: boardConfig.size.height }).map((_, y) => (
                <div
                  key={`left-${y}`}
                  className="text-[10px] text-gray-400 flex items-center justify-end pr-1 flex-shrink-0"
                  style={{ height: cellSize }}
                >
                  {y % 5 === 0 ? y : ''}
                </div>
              ))}
            </div>

            {/* Board Grid */}
            <div
              className="relative border border-gray-200"
              style={{ width: boardWidth, height: boardHeight }}
            >
              {boardConfig.grid.map((row, y) =>
                row.map((cell, x) => {
                  const color = cell ? perlerColors.find((c) => c.id === cell) : null
                  const isSelected = highlightColor === cell
                  const isDimmed = highlightColor && !isSelected

                  return (
                    <div
                      key={`${x}-${y}`}
                      className="absolute"
                      style={{
                        left: x * cellSize,
                        top: y * cellSize,
                        width: cellSize - 2,
                        height: cellSize - 2,
                        backgroundColor: color?.hex || '#f0f0f0',
                        opacity: isDimmed ? 0.12 : 1,
                        boxShadow: isSelected ? '0 0 0 2px #000' : 'none',
                        borderRadius: borderRadius,
                      }}
                    />
                  )
                })
              )}
            </div>
          </div>

          {/* Grid numbers - Bottom */}
          <div className="flex" style={{ marginLeft: gridLabelSize }}>
            {Array.from({ length: boardConfig.size.width }).map((_, x) => (
              <div
                key={`bottom-${x}`}
                className="text-[10px] text-gray-400 text-center flex-shrink-0"
                style={{ width: cellSize }}
              >
                {x % 5 === 0 ? x : ''}
              </div>
            ))}
          </div>

          {/* Grid numbers - Right */}
          <div
            className="absolute flex flex-col"
            style={{ right: -gridLabelSize, top: gridLabelSize }}
          >
            {Array.from({ length: boardConfig.size.height }).map((_, y) => (
              <div
                key={`right-${y}`}
                className="text-[10px] text-gray-400 flex items-center justify-start pl-1 flex-shrink-0"
                style={{ height: cellSize }}
              >
                {y % 5 === 0 ? y : ''}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Color Palette - Right Side */}
      <div className="w-20 bg-gray-50 border-l flex flex-col h-full">
        {/* Clear highlight */}
        <button
          onClick={() => setHighlightColor(null)}
          className={cn(
            "flex-shrink-0 mx-2 mt-3 px-2 py-1.5 rounded-lg border-2 flex items-center justify-center text-[10px] font-medium transition-all",
            !highlightColor
              ? "border-foreground bg-foreground text-white"
              : "border-border hover:border-muted-foreground bg-white"
          )}
        >
          全部
        </button>

        {/* Color List */}
        <div className="flex-1 overflow-y-auto py-2 px-1.5 space-y-1">
          {colorCounts.map(({ color, count }) => (
            <button
              key={color.id}
              onClick={() => toggleHighlightColor(color.id)}
              className={cn(
                "w-full flex flex-col items-center p-1 rounded-lg border-2 transition-all",
                highlightColor === color.id
                  ? "border-foreground shadow-md bg-white"
                  : "border-transparent hover:border-muted-foreground/50 bg-white"
              )}
            >
              <div
                className="w-6 h-6 rounded-full border border-black/10"
                style={{
                  backgroundColor: color.hex,
                  opacity: highlightColor && highlightColor !== color.id ? 0.2 : 1,
                }}
              />
              <span className="text-[9px] mt-0.5 font-medium leading-tight">{color.code}</span>
              <span className="text-[8px] text-muted-foreground leading-tight">×{count}</span>
            </button>
          ))}
        </div>

        {/* Bottom Info */}
        <div className="flex-shrink-0 px-2 py-2 border-t bg-white">
          <div className="text-[10px] text-center text-muted-foreground mb-2">
            {highlightColor ? (
              <span>
                <span className="font-medium text-foreground">
                  {perlerColors.find(c => c.id === highlightColor)?.code}
                </span>
                {' '}×{colorCounts.find(c => c.color.id === highlightColor)?.count || 0}
              </span>
            ) : (
              <span>{totalBeads} 个</span>
            )}
          </div>

          {/* Unlock Button */}
          <button
            onMouseDown={handleUnlockStart}
            onMouseUp={handleUnlockEnd}
            onMouseLeave={handleUnlockEnd}
            onTouchStart={handleUnlockStart}
            onTouchEnd={handleUnlockEnd}
            className="relative w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-black/80 text-white rounded-lg text-[10px]"
          >
            <div
              className="absolute inset-0 bg-primary rounded-lg transition-all"
              style={{ width: `${unlockProgress}%` }}
            />
            <Lock className="h-3 w-3 relative z-10" />
            <span className="relative z-10">解锁</span>
          </button>
        </div>
      </div>

      {/* Back Button */}
      <button
        onClick={() => {
          setLocked(false)
          navigate('/pixelate')
        }}
        className="fixed top-4 left-4 z-50 flex items-center gap-1 px-3 py-1.5 bg-black/60 text-white rounded-full text-sm backdrop-blur"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        <span>返回</span>
      </button>

      {/* Info Button */}
      <button
        onClick={() => setShowInfo(!showInfo)}
        className="fixed top-4 right-24 z-50 w-8 h-8 bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur"
      >
        <Info className="h-4 w-4" />
      </button>

        {/* Info Modal */}
        {showInfo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowInfo(false)}>
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-bold mb-3">使用说明</h3>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>• 右侧颜色栏：点击颜色可高亮该颜色</li>
                <li>• 高亮时其他颜色会变暗</li>
                <li>• 再次点击取消高亮</li>
                <li>• 右下角长按按钮可解锁返回</li>
              </ul>
              <Button className="w-full mt-4" onClick={() => setShowInfo(false)}>知道了</Button>
            </div>
          </div>
        )}
      </div>
    )
  }
